import jsPDF from 'jspdf';
import { authenticatedFetch } from './auth';
import { getUserMapPreference } from './mapUtils';
import QRCode from 'qrcode';

// Helper function to generate QR code data URL
const generateQRCodeDataURL = async (text) => {
  try {
    const qrDataURL = await QRCode.toDataURL(text, {
      width: 200,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
    return qrDataURL;
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw error;
  }
};

// Function to generate a real map using frontend canvas and tiles with user preferences
const generateRealMapCanvas = async (latitude, longitude, width, height, destinationLat = null, destinationLng = null, userMapPreference = 'roadmap', currentZoom = null, routePath = null) => {
  try {
    const mapType = userMapPreference === 'satellite' || userMapPreference === 'hybrid' ? 'satellite' : 'street';
    console.log(`🗺️ Generating real ${mapType} map with tiles for preference: ${userMapPreference}`);
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Clear canvas with loading background
    ctx.fillStyle = mapType === 'satellite' ? '#1a1a1a' : '#f0f8ff';
    ctx.fillRect(0, 0, width, height);
    
    // Calculate pixel positions for canvas center (needed for tile positioning)
    const centerPixelX = width / 2;
    const centerPixelY = height / 2;
    
    // Calculate optimal zoom level based on map type
    let zoom;
    const tileSize = 256;
    
    if (destinationLat && destinationLng) {
      // For route maps, calculate optimal zoom to fit both markers and route
      console.log(`📏 Route PDF - Calculating optimal zoom to fit entire route`);
      
      // Calculate bounding box
      let minLat = Math.min(latitude, destinationLat);
      let maxLat = Math.max(latitude, destinationLat);
      let minLng = Math.min(longitude, destinationLng);
      let maxLng = Math.max(longitude, destinationLng);
      
      // Expand bounding box to include all route points if available
      if (routePath && routePath.length > 0) {
        routePath.forEach(point => {
          minLat = Math.min(minLat, point[0]);
          maxLat = Math.max(maxLat, point[0]);
          minLng = Math.min(minLng, point[1]);
          maxLng = Math.max(maxLng, point[1]);
        });
        console.log(`📏 Expanded bounds to include ${routePath.length} route points`);
      }
      
      // Add padding to the bounding box (10% on each side)
      const latRange = maxLat - minLat;
      const lngRange = maxLng - minLng;
      const latPadding = Math.max(latRange * 0.1, 0.001); // Minimum padding
      const lngPadding = Math.max(lngRange * 0.1, 0.001);
      
      const paddedMinLat = minLat - latPadding;
      const paddedMaxLat = maxLat + latPadding;
      const paddedMinLng = minLng - lngPadding;
      const paddedMaxLng = maxLng + lngPadding;
      
      // Calculate zoom level to fit the bounding box in the canvas
      // Web Mercator projection: number of tiles at zoom level z = 2^z
      const latDegreeRange = paddedMaxLat - paddedMinLat;
      const lngDegreeRange = paddedMaxLng - paddedMinLng;
      
      // Calculate required zoom for latitude (Mercator projection is more complex for lat)
      const latZoom = Math.log2(height / tileSize * 360 / latDegreeRange);
      
      // Calculate required zoom for longitude (linear in Mercator)
      const lngZoom = Math.log2(width / tileSize * 360 / lngDegreeRange);
      
      // Use the more restrictive (smaller) zoom to ensure everything fits
      zoom = Math.max(1, Math.min(18, Math.floor(Math.min(latZoom, lngZoom))));
      
      console.log(`📏 Route bounds: lat[${minLat.toFixed(6)}, ${maxLat.toFixed(6)}], lng[${minLng.toFixed(6)}, ${maxLng.toFixed(6)}]`);
      console.log(`📏 Padded bounds: lat[${paddedMinLat.toFixed(6)}, ${paddedMaxLat.toFixed(6)}], lng[${paddedMinLng.toFixed(6)}, ${paddedMaxLng.toFixed(6)}]`);
      console.log(`📏 Calculated zoom levels: lat=${latZoom.toFixed(2)}, lng=${lngZoom.toFixed(2)}`);
      console.log(`📏 Using optimal zoom level: ${zoom} (auto-calculated for route fit)`);
      
    } else {
      // For single point maps, use current zoom from interactive map or default
      zoom = currentZoom || 16;
      console.log(`📏 Single point PDF - using zoom level: ${zoom} ${currentZoom ? '(from interactive map)' : '(default)'}`);
    }
    
    // Calculate center coordinates
    let centerLat, centerLng;
    if (destinationLat && destinationLng) {
      // For route maps, calculate optimal center based on route path if available
      if (routePath && routePath.length > 0) {
        console.log(`🗺️ Route map - calculating center from route path with ${routePath.length} points`);
        
        // Calculate bounding box of the entire route
        let minLat = Math.min(latitude, destinationLat);
        let maxLat = Math.max(latitude, destinationLat);
        let minLng = Math.min(longitude, destinationLng);
        let maxLng = Math.max(longitude, destinationLng);
        
        // Expand bounding box to include all route points
        routePath.forEach(point => {
          minLat = Math.min(minLat, point[0]);
          maxLat = Math.max(maxLat, point[0]);
          minLng = Math.min(minLng, point[1]);
          maxLng = Math.max(maxLng, point[1]);
        });
        
        // Center on the middle of the route bounding box
        centerLat = (minLat + maxLat) / 2;
        centerLng = (minLng + maxLng) / 2;
        
        console.log(`🗺️ Route bounds: lat[${minLat.toFixed(6)}, ${maxLat.toFixed(6)}], lng[${minLng.toFixed(6)}, ${maxLng.toFixed(6)}]`);
        console.log(`🗺️ Route center: ${centerLat.toFixed(6)}, ${centerLng.toFixed(6)}`);
      } else {
        // Fallback: center between origin and destination
        centerLat = (latitude + destinationLat) / 2;
        centerLng = (longitude + destinationLng) / 2;
        console.log(`🗺️ Route map - centering between origin and destination (no route path)`);
      }
    } else {
      // For single point maps, center on the marker location
      centerLat = latitude;
      centerLng = longitude;
      console.log(`🗺️ Single point map - centering on marker location: ${centerLat}, ${centerLng}`);
    }
    
    // Convert lat/lng to tile coordinates (precise, not floored) - MOVED TO OUTER SCOPE FOR latLngToPixel ACCESS
    const exactTileX = (centerLng + 180) / 360 * Math.pow(2, zoom);
    const exactTileY = (1 - Math.log(Math.tan(centerLat * Math.PI / 180) + 1 / Math.cos(centerLat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom);
    
    // Calculate tile grid center
    const tileX = Math.floor(exactTileX);
    const tileY = Math.floor(exactTileY);
    
    // Calculate fractional offset within the tile
    const fractionalOffsetX = exactTileX - tileX;
    const fractionalOffsetY = exactTileY - tileY;
    
    // Calculate how many tiles we need
    const tilesX = Math.ceil(width / tileSize) + 1;
    const tilesY = Math.ceil(height / tileSize) + 1;
    
    // Calculate starting tile - ensure center tile is in the middle of our grid
    const centerTileIndexX = Math.floor(tilesX / 2);
    const centerTileIndexY = Math.floor(tilesY / 2);
    const startTileX = tileX - centerTileIndexX;
    const startTileY = tileY - centerTileIndexY;
    
    console.log(`🗺️ Tile grid: ${tilesX}x${tilesY}, center tile will be at grid position: ${centerTileIndexX}, ${centerTileIndexY}`);
    
    console.log(`Loading ${tilesX}x${tilesY} ${mapType} tiles starting from ${startTileX},${startTileY}`);
    console.log(`🎯 Center: ${centerLat}, ${centerLng} -> exact tile: ${exactTileX}, ${exactTileY}`);
    console.log(`🎯 Fractional offset: ${fractionalOffsetX}, ${fractionalOffsetY}`);
    console.log(`🗺️ Target location should be at tile ${tileX}, ${tileY} (center tile)`);
    
    // Function to load a single tile
    const loadTile = (x, y) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
           // Select tile sources based on user preference (EXACTLY matching Leaflet interactive map URLs)
           let tileSources;
           if (mapType === 'satellite') {
             // Esri Satellite Tiles (EXACT same URL format as Leaflet: tile/{z}/{y}/{x})
             tileSources = [
               `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`
             ];
           } else {
             // OpenStreetMap Tiles (EXACT same URL format as Leaflet: {s}.tile.openstreetmap.org/{z}/{x}/{y}.png)
             tileSources = [
               `https://a.tile.openstreetmap.org/${zoom}/${x}/${y}.png`,
               `https://b.tile.openstreetmap.org/${zoom}/${x}/${y}.png`,
               `https://c.tile.openstreetmap.org/${zoom}/${x}/${y}.png`
             ];
           }
        
        let sourceIndex = 0;
        
        const tryNextSource = () => {
          if (sourceIndex >= tileSources.length) {
            console.log(`Failed to load tile ${x}/${y} from all sources`);
            resolve(null);
            return;
          }
          
          const currentUrl = tileSources[sourceIndex++];
          console.log(`🔗 Loading tile ${x}/${y} from: ${currentUrl}`);
          img.src = currentUrl;
        };
        
        img.onload = () => {
          console.log(`✅ Loaded tile ${x}/${y}`);
          resolve(img);
        };
        
        img.onerror = () => {
          console.log(`❌ Failed to load tile ${x}/${y} from source ${sourceIndex}`);
          setTimeout(tryNextSource, 100); // Small delay before trying next source
        };
        
        tryNextSource();
      });
    };
    
    // Load all tiles
    const tilePromises = [];
    const tilePositions = [];
    
    for (let y = 0; y < tilesY; y++) {
      for (let x = 0; x < tilesX; x++) {
        const tileXPos = startTileX + x;
        const tileYPos = startTileY + y;
        
        if (tileXPos >= 0 && tileYPos >= 0 && tileXPos < Math.pow(2, zoom) && tileYPos < Math.pow(2, zoom)) {
          tilePromises.push(loadTile(tileXPos, tileYPos));
          // Position tiles to ensure center tile is properly centered
          // Calculate position relative to center tile
          const relativeX = x - centerTileIndexX;
          const relativeY = y - centerTileIndexY;
          
          // Position tile relative to canvas center, accounting for fractional offset
          const pixelX = centerPixelX + (relativeX - fractionalOffsetX) * tileSize;
          const pixelY = centerPixelY + (relativeY - fractionalOffsetY) * tileSize;
          
          // Debug center tile positioning
          if (tileXPos === tileX && tileYPos === tileY) {
            console.log(`🎯 CENTER TILE ${tileXPos},${tileYPos} positioned at pixel: ${pixelX}, ${pixelY}`);
            console.log(`🎯 Canvas center is at: ${centerPixelX}, ${centerPixelY}`);
            console.log(`🎯 Center tile offset from canvas center: ${pixelX - centerPixelX}, ${pixelY - centerPixelY}`);
          }
          
          tilePositions.push({ x: pixelX, y: pixelY, tileX: tileXPos, tileY: tileYPos });
        }
      }
    }
    
    // Wait for tiles to load (with timeout)
    console.log('⏳ Waiting for tiles to load...');
    const loadedTiles = await Promise.all(tilePromises.map(p => 
      Promise.race([
        p,
        new Promise(resolve => setTimeout(() => resolve(null), 5000)) // 5 second timeout per tile
      ])
    ));
    
    // Draw loaded tiles
    let tilesDrawn = 0;
    loadedTiles.forEach((tile, index) => {
      if (tile && tilePositions[index]) {
        const pos = tilePositions[index];
        // Draw tiles without offset to match standard tile positioning
        ctx.drawImage(tile, pos.x, pos.y);
        tilesDrawn++;
      }
    });
    
    console.log(`✅ Drew ${tilesDrawn} tiles out of ${loadedTiles.length} requested`);
    
    // If no tiles loaded, fall back to a simple background
    if (tilesDrawn === 0) {
      console.log('⚠️ No tiles loaded, using fallback background');
      ctx.fillStyle = '#e8f4f8';
      ctx.fillRect(0, 0, width, height);
      
      // Draw a simple street pattern as fallback
      ctx.strokeStyle = '#d0d0d0';
      ctx.lineWidth = 1;
      for (let x = 0; x < width; x += 50) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += 50) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    }
    
    // Pixel positions for markers (already calculated above)
    
    // Helper function to convert lat/lng to pixel coordinates using the same system as tiles
    const latLngToPixel = (lat, lng) => {
      console.log(`🎯 Converting coordinates: ${lat}, ${lng} to pixels`);
      
      // For single point maps, center the marker exactly 
      if (!destinationLat || !destinationLng) {
        console.log(`🎯 Single point map detected - placing marker at canvas center`);
        return { x: centerPixelX, y: centerPixelY };
      }
      
      // For route maps, use the EXACT same coordinate system as the tiles
      console.log(`🎯 Route map - using exact tile-based coordinate system`);
      
      // Convert lat/lng to exact tile coordinates (Web Mercator projection) 
      const pointTileX = (lng + 180) / 360 * Math.pow(2, zoom);
      const pointTileY = (1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom);
      
      console.log(`🎯 Point tile coords: ${pointTileX.toFixed(6)}, ${pointTileY.toFixed(6)}`);
      console.log(`🎯 Map center tile coords: ${exactTileX.toFixed(6)}, ${exactTileY.toFixed(6)}`);
      console.log(`🎯 Start tile coords: ${startTileX}, ${startTileY}`);
      console.log(`🎯 Fractional offset: ${fractionalOffsetX.toFixed(6)}, ${fractionalOffsetY.toFixed(6)}`);
      
      // CRITICAL: Use the EXACT same calculation as tile positioning
      // The tile calculation is: pixelX = centerPixelX + (relativeX - fractionalOffsetX) * tileSize
      // where relativeX = gridX - centerTileIndexX
      
      // Convert point tile coordinates to grid coordinates (same as tiles do)
      const gridX = pointTileX - startTileX;  // Grid position in our tile array
      const gridY = pointTileY - startTileY;
      
      // Calculate relative position from center tile (EXACT same as tiles)
      const relativeX = gridX - centerTileIndexX;
      const relativeY = gridY - centerTileIndexY;
      
      console.log(`🎯 Grid position: ${gridX.toFixed(6)}, ${gridY.toFixed(6)}`);
      console.log(`🎯 Center tile index: ${centerTileIndexX}, ${centerTileIndexY}`);
      console.log(`🎯 Relative to center: ${relativeX.toFixed(6)}, ${relativeY.toFixed(6)}`);
      
      // Apply EXACT same formula as tiles
      const x = centerPixelX + (relativeX - fractionalOffsetX) * tileSize;
      const y = centerPixelY + (relativeY - fractionalOffsetY) * tileSize;
      
      console.log(`🎯 Final pixel position: ${x.toFixed(2)}, ${y.toFixed(2)} (canvas center: ${centerPixelX}, ${centerPixelY})`);
      
      // Validate the calculation
      const distanceFromCenter = Math.sqrt(Math.pow(x - centerPixelX, 2) + Math.pow(y - centerPixelY, 2));
      console.log(`🎯 Distance from canvas center: ${distanceFromCenter.toFixed(2)} pixels`);
      
      return { x, y };
    };
    
           // Draw origin marker (same emoji as interactive map)
           const originPixel = latLngToPixel(latitude, longitude);
           
           console.log(`📍 Drawing origin marker at pixel: ${originPixel.x}, ${originPixel.y} (canvas center: ${centerPixelX}, ${centerPixelY})`);
           
           // For single point maps, the marker should be near the center
           if (!destinationLat && !destinationLng) {
             const distanceFromCenter = Math.sqrt(Math.pow(originPixel.x - centerPixelX, 2) + Math.pow(originPixel.y - centerPixelY, 2));
             console.log(`📍 Distance from canvas center: ${distanceFromCenter.toFixed(2)} pixels`);
           }
           
           // Draw emoji marker matching interactive map (📍)
           ctx.font = '32px Arial, sans-serif';
           ctx.textAlign = 'center';
           ctx.textBaseline = 'bottom';
           
           // Add white outline for better visibility
           ctx.strokeStyle = 'white';
           ctx.lineWidth = 4;
           ctx.strokeText('📍', originPixel.x, originPixel.y);
           
           // Draw the emoji
           ctx.fillStyle = '#000';
           ctx.fillText('📍', originPixel.x, originPixel.y);
    
    // Draw destination marker if provided
    if (destinationLat && destinationLng) {
      const destPixel = latLngToPixel(destinationLat, destinationLng);
      
      // Draw route path if available (actual road route), otherwise draw straight line
      if (routePath && routePath.length > 0) {
        console.log('🛣️ Drawing actual route path with', routePath.length, 'points');
        ctx.strokeStyle = '#4285F4';
        ctx.lineWidth = 3;
        ctx.beginPath();
        
        // Draw the actual route path
        routePath.forEach((point, index) => {
          const routePixel = latLngToPixel(point[0], point[1]); // [lat, lng] format
          
          // Debug first and last points
          if (index === 0) {
            console.log(`🛣️ Route start pixel: ${routePixel.x}, ${routePixel.y}`);
            ctx.moveTo(routePixel.x, routePixel.y);
          } else if (index === routePath.length - 1) {
            console.log(`🛣️ Route end pixel: ${routePixel.x}, ${routePixel.y}`);
            ctx.lineTo(routePixel.x, routePixel.y);
          } else {
            ctx.lineTo(routePixel.x, routePixel.y);
          }
        });
        ctx.stroke();
        console.log('🛣️ Route path drawn successfully');
      } else {
        console.log('📏 No route path available, drawing straight line');
        // Draw straight line between points (fallback)
        ctx.strokeStyle = '#4285F4';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(originPixel.x, originPixel.y);
        ctx.lineTo(destPixel.x, destPixel.y);
        ctx.stroke();
      }
      
           // Draw destination marker (same emoji as interactive map)
           ctx.font = '32px Arial, sans-serif';
           ctx.textAlign = 'center';
           ctx.textBaseline = 'bottom';
           
           // Add white outline for better visibility
           ctx.strokeStyle = 'white';
           ctx.lineWidth = 4;
           ctx.strokeText('🎯', destPixel.x, destPixel.y);
           
           // Draw the emoji
           ctx.fillStyle = '#000';
           ctx.fillText('🎯', destPixel.x, destPixel.y);
    }
    
    // Add coordinate overlay
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(5, 5, 240, 25);
    ctx.fillStyle = '#333';
    ctx.font = '12px Arial';
    ctx.fillText(`📍 ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`, 10, 20);
    
           // Add attribution (EXACTLY matching interactive map attributions)
           ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
           const attributionText = mapType === 'satellite' ? '© Esri, Maxar, Earthstar Geographics' : '© OpenStreetMap contributors';
           const textWidth = mapType === 'satellite' ? 200 : 150;
           ctx.fillRect(width - textWidth - 10, height - 20, textWidth + 5, 15);
           ctx.fillStyle = '#666';
           ctx.font = '10px Arial';
           ctx.fillText(attributionText, width - textWidth - 5, height - 8);
    
    console.log(`✅ Real ${mapType} map generated successfully`);
    return canvas.toDataURL('image/png');
    
  } catch (error) {
    console.error('❌ Error generating real map:', error);
    return null;
  }
};

// Unified PDF generation utility for all use cases
export const generateUnifiedPDF = async (data, options = {}) => {
  const {
    type = 'single', // 'single', 'route', 'multiple'
    calculateRoute = null, // Function to calculate route if needed
    userEmail = null
  } = options;

  console.log('Unified PDF Generation - Type:', type, 'Data:', data);

  try {
    let searchRecord;

    switch (type) {
      case 'single':
        searchRecord = createSinglePointRecord(data, userEmail);
        break;
      
      case 'route':
        searchRecord = await createRouteRecord(data, userEmail, calculateRoute);
        break;
      
      case 'multiple':
        searchRecord = createMultipleAddressRecord(data, userEmail);
        break;
      
      default:
        throw new Error(`Unknown PDF type: ${type}`);
    }

            console.log('Generated search record:', searchRecord);
        return await generateSearchEvidencePDF(searchRecord);
  } catch (error) {
    console.error('Error in unified PDF generation:', error);
    throw error;
  }
};

// Create search record for single point (Digipin search)
const createSinglePointRecord = (data, userEmail) => {
  console.log('createSinglePointRecord - Input data:', data);
  
  // Ensure we have a proper address - avoid generic fallbacks like "Search Result"
  let address = data.address;
  
  // Check if the address is just coordinates (lat,lng format)
  const isCoordinatesOnly = (addr) => {
    if (!addr || typeof addr !== 'string') return false;
    // Check if it matches lat,lng pattern (numbers with comma)
    const coordPattern = /^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/;
    return coordPattern.test(addr.trim());
  };
  
  // Check if the address is a generic fallback that we should replace
  const isGenericFallback = (addr) => {
    if (!addr || typeof addr !== 'string') return true;
    const genericTerms = ['Search Result', 'Selected Location', 'Search Marker', 'Marker Location', 'Unknown Location'];
    return genericTerms.includes(addr.trim());
  };
  
  // If address is empty, undefined, coordinates only, or a generic fallback, try alternatives
  if (!address || address.trim() === '' || isCoordinatesOnly(address) || isGenericFallback(address)) {
    console.log('Address is empty, coordinates, or generic fallback, trying alternatives...');
    console.log('Original address:', address);
    
    // Try search_input first (this might have the original search term)
    if (data.search_input && data.search_input !== 'Unknown' && !isCoordinatesOnly(data.search_input) && !isGenericFallback(data.search_input)) {
      address = data.search_input;
      console.log('Using search_input as address:', address);
    } 
    // Try any other address fields
    else if (data.display_name && !isCoordinatesOnly(data.display_name) && !isGenericFallback(data.display_name)) {
      address = data.display_name;
      console.log('Using display_name as address:', address);
    }
    else if (data.place_name && !isCoordinatesOnly(data.place_name) && !isGenericFallback(data.place_name)) {
      address = data.place_name;
      console.log('Using place_name as address:', address);
    }
    // Try original search value if it's not coordinates
    else if (data.value && !isCoordinatesOnly(data.value) && !isGenericFallback(data.value)) {
      address = data.value;
      console.log('Using value as address:', address);
    }
    // Last resort - use coordinates but format them nicely
    else if (data.lat && data.lng) {
      address = `Location: ${Number(data.lat).toFixed(6)}, ${Number(data.lng).toFixed(6)}`;
      console.log('Using formatted coordinates as address:', address);
    } else {
      address = 'Unknown Location';
      console.log('Using final fallback address:', address);
    }
  }
  
  console.log('Final address for PDF:', address);

  const searchRecord = {
    id: `single_${Date.now()}`,
    search_input: data.search_input || data.address || data.digipin || 'Unknown',
    address: address,
    digipin: data.digipin || '',
    latitude: data.lat || data.latitude || null,
    longitude: data.lng || data.longitude || null,
    country_code: 'IN',
    country_name: 'India',
    search_type: 'single_point',
    user_email: userEmail,
    user_id: userEmail,
    timestamp: new Date().toISOString(),
    isSinglePoint: true,
    source: 'digipin_search',
    currentZoom: data.currentZoom // Preserve the current zoom level from interactive map
  };

  console.log('createSinglePointRecord - Generated search record:', searchRecord);
  return searchRecord;
};

// Create search record for route (Route Optimizer, Pin2Point nearest location)
const createRouteRecord = async (data, userEmail, calculateRoute = null) => {
  const {
    fromData,
    toData,
    distanceData,
    routePath = null,
    source = 'route_optimizer'
    // Note: currentZoom removed - auto-calculated for routes in map generation
  } = data;

  // Calculate route if function provided and no route path exists
  let finalRoutePath = routePath;
  if (calculateRoute && !routePath) {
    try {
      const origin = `${fromData.lat},${fromData.lng}`;
      const destination = `${toData.lat},${toData.lng}`;
      const customAddresses = {
        origin: fromData.address,
        destination: toData.address
      };
      
      await calculateRoute(origin, destination, customAddresses);
      finalRoutePath = window.currentRoutePath || null;
    } catch (error) {
      console.warn('Route calculation failed, proceeding without route path:', error);
    }
  }

  return {
    id: `route_${Date.now()}`,
    search_input: `${fromData.address} to ${toData.address}`,
    address: fromData.address,
    digipin: fromData.digipin || 'N/A',
    latitude: fromData.lat,
    longitude: fromData.lng,
    road_distance: distanceData.road_distance_km,
    aerial_distance: distanceData.aerial_distance_km,
    duration: distanceData.road_duration,
    destination_address: toData.address,
    destination_label: toData.label || toData.address, // Add destination label (e.g., "Mumbai Branch")
    destination_digipin: toData.digipin || 'N/A',
    destination_lat: toData.lat,
    destination_lng: toData.lng,
    country_code: 'IN',
    country_name: 'India',
    search_type: 'route',
    user_email: userEmail,
    user_id: userEmail,
    timestamp: new Date().toISOString(),
    isSinglePoint: false,
    isRouteSearch: true,
    routePath: finalRoutePath,
    source: source
    // Note: currentZoom removed - auto-calculated in map generation for routes
  };
};

// Create search record for multiple addresses (Bulk operations)
const createMultipleAddressRecord = (data, userEmail) => {
  const { addresses, searchType = 'bulk_search' } = data;
  
  return {
    id: `bulk_${Date.now()}`,
    search_input: `Bulk search - ${addresses.length} addresses`,
    addresses: addresses,
    country_code: 'IN',
    country_name: 'India',
    search_type: 'multiple_addresses',
    user_email: userEmail,
    user_id: userEmail,
    timestamp: new Date().toISOString(),
    isSinglePoint: false,
    isRouteSearch: false,
    source: searchType
  };
};

// Legacy function for backward compatibility
export const createDigipinSearchRecord = (data, userEmail) => {
  return createSinglePointRecord(data, userEmail);
};

// Legacy function for backward compatibility
export const createSearchRecord = (data, userEmail) => {
  return createSinglePointRecord(data, userEmail);
};


// Function to generate a PDF for a search record
export const generateSearchEvidencePDF = async (searchRecord) => {
  console.log('Starting PDF generation for:', searchRecord);
  console.log('Search record coordinates - latitude:', searchRecord.latitude, 'longitude:', searchRecord.longitude);
  console.log('Search record isSinglePoint:', searchRecord.isSinglePoint);
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (2 * margin);
    
    let yPosition = margin;
    
    // Add Pin2Point logo and title
    pdf.setFontSize(20);
    pdf.setTextColor(0, 123, 255); // Blue color
    pdf.text('Pin2Point', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 12;
    
    // Removed "Route Optimization Report" text to make it generic for all PDF types
    yPosition += 8;
    
    // Report Information (compact)
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text('Report Information', margin, yPosition);
    yPosition += 8;
    
    pdf.setFontSize(9);
    pdf.setFont(undefined, 'normal');
    
    const searchId = `Report ID: ${searchRecord.id || 'N/A'}`;
    
    // Format timestamp properly
    const now = new Date(searchRecord.timestamp);
    const date = now.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
    const time = now.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: true 
    });
    const timestamp = `Date & Time: ${date}, ${time}`;
    
    const userId = `User ID: ${searchRecord.user_id || 'N/A'}`;
    
    pdf.text(searchId, margin, yPosition);
    yPosition += 5;
    pdf.text(timestamp, margin, yPosition);
    yPosition += 5;
    pdf.text(userId, margin, yPosition);
    yPosition += 12;
    
         // Addresses side by side with proper alignment
     const leftColumn = margin;
     const rightColumn = margin + (contentWidth / 2) + 5;
     const columnWidth = (contentWidth / 2) - 5;
     
     // Calculate starting Y position for both columns
     const startY = yPosition;
     
           // Add QR code for first address (above the address section)
      if (searchRecord.latitude && searchRecord.longitude) {
        try {
          const googleMapsUrl1 = `https://maps.google.com/maps?q=${searchRecord.latitude},${searchRecord.longitude}&z=15`;
          const qrDataURL1 = await generateQRCodeDataURL(googleMapsUrl1);
          
          // Calculate QR code dimensions (15mm x 15mm)
          const qrSize = 15;
          const qrX1 = leftColumn + columnWidth - qrSize; // Position at the right edge of left column
          const qrY1 = startY - 5; // Position above the address section
          
          // Add QR code to PDF
          pdf.addImage(qrDataURL1, 'PNG', qrX1, qrY1, qrSize, qrSize);
          
          // Add small description below QR code
          pdf.setFontSize(7);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(0, 0, 255); // Blue color
          pdf.text('Scan for Location', qrX1 + qrSize/2, qrY1 + qrSize + 2, { align: 'center' });
          pdf.setTextColor(0, 0, 0); // Reset to black
          
          console.log('QR code added for first address');
        } catch (qrError) {
          console.error('Error generating QR code for first address:', qrError);
        }
      }
      
      // Address 1 (Left Column) - Selected Location
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Selected Location', leftColumn, startY);
      
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'normal');
      
      const address1 = `Address: ${searchRecord.address || 'N/A'}`;
      const digipin1 = `DigiPin: ${searchRecord.digipin || 'N/A'}`;
      const coordinates1 = `Coordinates: ${searchRecord.latitude || 'N/A'}, ${searchRecord.longitude || 'N/A'}`;
      
             // Wrap text for address and track position with hyperlink
       // Reserve space for QR code on the right side of the column
       const qrReservedSpace = 20; // 20mm for QR code + spacing
       const address1ColumnWidth = columnWidth - qrReservedSpace; // Reserve space for QR code
       const address1Lines = pdf.splitTextToSize(address1, address1ColumnWidth);
       let leftY = startY + 8;
       address1Lines.forEach(line => {
        if (searchRecord.latitude && searchRecord.longitude) {
          // Add clickable link for each line
          const googleMapsUrl1 = `https://maps.google.com/maps?q=${searchRecord.latitude},${searchRecord.longitude}&z=15`;
          const linkWidth1 = pdf.getTextWidth(line);
          pdf.setTextColor(0, 0, 255); // Blue color for link
          pdf.text(line, leftColumn, leftY);
          pdf.link(leftColumn, leftY - 4, linkWidth1, 4, { url: googleMapsUrl1 });
          // Add a subtle underline for the link
          pdf.setDrawColor(0, 0, 255);
          pdf.line(leftColumn, leftY + 1, leftColumn + linkWidth1, leftY + 1);
          pdf.setDrawColor(0, 0, 0); // Reset draw color
          pdf.setTextColor(0, 0, 0); // Reset to black
        } else {
          // If no coordinates, just show the address as regular text
          pdf.text(line, leftColumn, leftY);
        }
        leftY += 4;
      });
      
      pdf.text(digipin1, leftColumn, leftY);
      leftY += 4;
      pdf.text(coordinates1, leftColumn, leftY);
      leftY += 8;
     
           // Address 2 (Right Column) - if available
      if (searchRecord.destination_address) {
        // Add QR code for second address (above the address section)
        if (searchRecord.destination_lat && searchRecord.destination_lng) {
          try {
            const googleMapsUrl2 = `https://maps.google.com/maps?q=${searchRecord.destination_lat},${searchRecord.destination_lng}&z=15`;
            const qrDataURL2 = await generateQRCodeDataURL(googleMapsUrl2);
            
            // Calculate QR code dimensions (15mm x 15mm)
            const qrSize = 15;
            const qrX2 = rightColumn + columnWidth - qrSize; // Position at the right edge of right column
            const qrY2 = startY - 5; // Position above the address section
            
            // Add QR code to PDF
            pdf.addImage(qrDataURL2, 'PNG', qrX2, qrY2, qrSize, qrSize);
            
            // Add small description below QR code
            pdf.setFontSize(7);
            pdf.setFont(undefined, 'normal');
            pdf.setTextColor(0, 0, 255); // Blue color
            pdf.text('Scan for Location', qrX2 + qrSize/2, qrY2 + qrSize + 2, { align: 'center' });
            pdf.setTextColor(0, 0, 0); // Reset to black
            
            console.log('QR code added for second address');
          } catch (qrError) {
            console.error('Error generating QR code for second address:', qrError);
          }
        }
        
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        // Use the actual destination label (e.g., "Mumbai Branch") instead of generic "My Location"
        const destinationLabel = searchRecord.destination_label || 'My Location';
        pdf.text(destinationLabel, rightColumn, startY);
        
        pdf.setFontSize(9);
        pdf.setFont(undefined, 'normal');
        
        const address2 = `Address: ${searchRecord.destination_address || 'N/A'}`;
        const digipin2 = `DigiPin: ${searchRecord.destination_digipin || 'N/A'}`;
        const coordinates2 = `Coordinates: ${searchRecord.destination_lat || 'N/A'}, ${searchRecord.destination_lng || 'N/A'}`;
        
                 // Wrap text for address and track position with hyperlink
         // Reserve space for QR code on the right side of the column
         const qrReservedSpace2 = 20; // 20mm for QR code + spacing
         const address2ColumnWidth = columnWidth - qrReservedSpace2; // Reserve space for QR code
         const address2Lines = pdf.splitTextToSize(address2, address2ColumnWidth);
         let rightY = startY + 8;
         address2Lines.forEach(line => {
          if (searchRecord.destination_lat && searchRecord.destination_lng) {
            // Add clickable link for each line
            const googleMapsUrl2 = `https://maps.google.com/maps?q=${searchRecord.destination_lat},${searchRecord.destination_lng}&z=15`;
            const linkWidth2 = pdf.getTextWidth(line);
            pdf.setTextColor(0, 0, 255); // Blue color for link
            pdf.text(line, rightColumn, rightY);
            pdf.link(rightColumn, rightY - 4, linkWidth2, 4, { url: googleMapsUrl2 });
            // Add a subtle underline for the link
            pdf.setDrawColor(0, 0, 255);
            pdf.line(rightColumn, rightY + 1, rightColumn + linkWidth2, rightY + 1);
            pdf.setDrawColor(0, 0, 0); // Reset draw color
            pdf.setTextColor(0, 0, 0); // Reset to black
          } else {
            // If no coordinates, just show the address as regular text
            pdf.text(line, rightColumn, rightY);
          }
          rightY += 4;
        });
        
        pdf.text(digipin2, rightColumn, rightY);
        rightY += 4;
        pdf.text(coordinates2, rightColumn, rightY);
        rightY += 8;
       
       // Use the higher Y position from both columns
       yPosition = Math.max(leftY, rightY);
     } else {
       yPosition = leftY;
     }
    
    // Distance information (compact)
    if (searchRecord.road_distance || searchRecord.aerial_distance) {
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Distance Information', margin, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'normal');
      
             const roadDistance = `Road Distance: ${searchRecord.road_distance ? `${searchRecord.road_distance} km` : 'N/A'}`;
       const aerialDistance = `Aerial Distance: ${searchRecord.aerial_distance ? `${searchRecord.aerial_distance} km` : 'N/A'}`;
       const duration = `Duration: ${searchRecord.duration || 'N/A'}`;
       
       // Add hyperlink to Road Distance (only for route searches)
       if (searchRecord.destination_lat && searchRecord.destination_lng && searchRecord.road_distance) {
         const directionsUrl = `https://maps.google.com/maps/dir/${searchRecord.latitude},${searchRecord.longitude}/${searchRecord.destination_lat},${searchRecord.destination_lng}`;
         const linkWidth = pdf.getTextWidth(roadDistance);
         
         pdf.setTextColor(0, 0, 255); // Blue color for link
         pdf.text(roadDistance, margin, yPosition);
         pdf.link(margin, yPosition - 4, linkWidth, 4, { url: directionsUrl });
         // Add a subtle underline for the link
         pdf.setDrawColor(0, 0, 255);
         pdf.line(margin, yPosition + 1, margin + linkWidth, yPosition + 1);
         pdf.setDrawColor(0, 0, 0); // Reset draw color
         pdf.setTextColor(0, 0, 0); // Reset to black
       } else {
         pdf.text(roadDistance, margin, yPosition);
       }
       yPosition += 4;
       pdf.text(aerialDistance, margin, yPosition);
       yPosition += 4;
       pdf.text(duration, margin, yPosition);
       yPosition += 10;
    }
    
         // Add QR code with Google Maps link if coordinates are available (only for route searches)
     if (searchRecord.latitude && searchRecord.longitude && searchRecord.destination_lat && searchRecord.destination_lng) {
       try {
         // Route between two points
         const googleMapsUrl = `https://maps.google.com/maps/dir/${searchRecord.latitude},${searchRecord.longitude}/${searchRecord.destination_lat},${searchRecord.destination_lng}`;
         console.log('Generating QR code for Google Maps URL:', googleMapsUrl);
         
         // Generate QR code
         const qrDataURL = await generateQRCodeDataURL(googleMapsUrl);
         
         // Calculate QR code dimensions (15mm x 15mm for compact layout)
         const qrSize = 15;
         const qrX = pageWidth - margin - qrSize; // Position on the right side
         const qrY = yPosition - 10; // Align with the distance information
         
         // Add QR code to PDF
         pdf.addImage(qrDataURL, 'PNG', qrX, qrY, qrSize, qrSize);
         
         // Add small description below QR code
         pdf.setFontSize(7);
         pdf.setFont(undefined, 'normal');
         pdf.setTextColor(0, 0, 255); // Blue color
         pdf.text('Scan for Route', qrX + qrSize/2, qrY + qrSize + 2, { align: 'center' });
         pdf.setTextColor(0, 0, 0); // Reset to black
         
         console.log('QR code added to PDF successfully');
       } catch (qrError) {
         console.error('Error generating QR code:', qrError);
         // Continue without QR code if there's an error
         console.log('Skipping QR code section due to error');
       }
     }
    
    // Generate map snapshot if coordinates are available
    if (searchRecord.latitude && searchRecord.longitude) {
      try {
        console.log('Generating map for coordinates:', searchRecord.latitude, searchRecord.longitude);
        console.log('Search type:', searchRecord.isSinglePoint ? 'Single Point (Digipin)' : 'Route (Route Optimizer)');
        
        // Add map to PDF
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        const mapTitle = searchRecord.destination_lat ? 'Route Map' : 'Location Map';
        pdf.text(mapTitle, margin, yPosition);
        yPosition += 8;
        
        // Calculate map dimensions to fit on page
        const mapWidth = contentWidth;
        const mapHeight = Math.min((mapWidth * 400) / 600, pageHeight - yPosition - margin - 10); // Ensure it fits
        
        // Generate real map directly in frontend using canvas and tiles (street/satellite based on user preference)
        try {
          console.log('Generating real map using frontend canvas with user preferences');
          
          // Fetch user's map preference
          let userMapPreference = 'roadmap'; // Default fallback
          try {
            const preference = await getUserMapPreference();
            userMapPreference = preference.map_view_preference || 'roadmap';
            console.log('User map preference for PDF:', userMapPreference);
          } catch (prefError) {
            console.log('Failed to fetch user map preference, using default (roadmap):', prefError);
          }
          
          const mapImageDataUrl = await generateRealMapCanvas(
            searchRecord.latitude,
            searchRecord.longitude,
            600,
            400,
            searchRecord.destination_lat,
            searchRecord.destination_lng,
            userMapPreference,
            searchRecord.currentZoom, // Pass the current zoom level from interactive map
            searchRecord.routePath // Pass the route path for actual road route display
          );
          
          if (mapImageDataUrl) {
            pdf.addImage(mapImageDataUrl, 'PNG', margin, yPosition, mapWidth, mapHeight);
            const mapType = userMapPreference === 'satellite' || userMapPreference === 'hybrid' ? 'satellite' : 'street';
            console.log(`Real ${mapType} map image added to PDF successfully`);
            yPosition += mapHeight + 5;
          } else {
            console.log('Real map generation failed, skipping map section');
          }
        } catch (mapError) {
          console.error('Real map generation failed:', mapError);
          console.log('Skipping map section due to map error');
        }
      } catch (mapError) {
        console.error('Error generating map:', mapError);
        // Skip map section if there's an error
        console.log('Skipping map section due to error');
      }
    }
    
    // Add footer
    pdf.setFontSize(7);
    pdf.setTextColor(100, 100, 100);
    const footerText = `Generated on ${new Date().toLocaleString()} by Pin2Point Platform`;
    pdf.text(footerText, pageWidth / 2, pageHeight - 8, { align: 'center' });
    
    // Save the PDF
    const fileName = `pin2point_route_${searchRecord.id || Date.now()}.pdf`;
    pdf.save(fileName);
    
    return fileName;
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF evidence');
  }
};


